<main>
  <loading-overlay [isLoading]="isLoading() > 0 || !enumsReady()" />
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'PoSettlement_Title' | translateWF: 'PO Settlement' }}</h1>
        <div class="page__title_buttons"></div>
      </div>
    </div>

    @if (summary(); as s) {
      <div class="cs-summary-grid">
        <p-card data-variant="summary" [header]="'PoSettlement_TotalRequested' | translateWF: 'Total Requested'">
          <ng-template pTemplate="subtitle">{{ s.totalRequested | number: '1.2-2' }} {{ currencyLabel(s.currency) }}</ng-template>
          <div>{{ s.requestedPoCount }} {{ 'PoSettlement_PosWithRequests' | translateWF: 'POs with requests' }}</div>
        </p-card>
        <p-card data-variant="summary" data-severity="success" [header]="'PoSettlement_TotalPaid' | translateWF: 'Total Paid'">
          <ng-template pTemplate="subtitle">{{ s.totalPaid | number: '1.2-2' }} {{ currencyLabel(s.currency) }}</ng-template>
          <div>{{ 'PoSettlement_PostedPayments' | translateWF: 'Posted payments' }}</div>
        </p-card>
        <p-card data-variant="summary" [header]="'PoSettlement_OpenBalance' | translateWF: 'Open Balance'">
          <ng-template pTemplate="subtitle">{{ s.openBalance | number: '1.2-2' }} {{ currencyLabel(s.currency) }}</ng-template>
          <div>{{ 'PoSettlement_NotYetPaid' | translateWF: 'Invoiced total not yet paid' }}</div>
        </p-card>
        <p-card data-variant="summary" [header]="'PoSettlement_Settled' | translateWF: 'Settled'">
          <ng-template pTemplate="subtitle">{{ s.settledCount }} / {{ s.totalCount }}</ng-template>
          <div>{{ 'PoSettlement_FullyPaid' | translateWF: 'Fully paid purchase orders' }}</div>
        </p-card>
      </div>
    }

    <div class="cs-page-list-grid">
      @if (filterConfig() && showFilters()) {
        <div>
          <csl-dynamic-filters [filterConfig]="filterConfig()!" [state]="filterState" />
        </div>
      }

      <p-panel [header]="'PoSettlement_Title' | translateWF: 'PO Settlement'">
        <ng-template pTemplate="icons">
          <p-button
            [label]="(showFilters() ? 'Common_Hide_Filters' : 'Common_Show_Filters') | translateWF: (showFilters() ? 'Hide Filters' : 'Show Filters')"
            [icon]="showFilters() ? 'pi pi-filter-slash' : 'pi pi-filter'"
            (click)="showFilters.set(!showFilters())"
            variant="outlined"
            size="small"
          />
        </ng-template>
        <p-card data-variant="compact">
          <p-table [value]="rows()" selectionMode="single" [lazy]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ 'PoSettlement_PoNumber' | translateWF: 'Purchase order' }}</th>
                <th>{{ 'PoSettlement_Provider' | translateWF: 'Provider' }}</th>
                <th class="text-right">{{ 'PoSettlement_Docs' | translateWF: 'Docs' }}</th>
                <th class="text-right">{{ 'PoSettlement_Total' | translateWF: 'Total' }}</th>
                <th class="text-right">{{ 'PoSettlement_Requested' | translateWF: 'Requested' }}</th>
                <th class="text-right">{{ 'PoSettlement_Paid' | translateWF: 'Paid' }}</th>
                <th class="text-right">{{ 'PoSettlement_Balance' | translateWF: 'Balance' }}</th>
                <th style="width: 9rem">{{ 'PoSettlement_SettlementCol' | translateWF: 'Settlement' }}</th>
                <th>{{ 'Status' | translateWF: 'Status' }}</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-rowData>
              @let row = getRowData(rowData);
              <tr>
                <td>
                  <a class="cs-table-link" (click)="redirectToDetails(row.purchaseOrderId)">{{ row.purchaseOrderNumber }}</a>
                  <div class="po-settlement__subtle">{{ row.issuedDate | date: 'dd/MM/yyyy' }}</div>
                </td>
                <td>{{ row.providerName }}</td>
                <td class="text-right">{{ row.documentCount || '—' }}</td>
                <td class="text-right">{{ row.invoiced | number: '1.2-2' }}</td>
                <td class="text-right">{{ row.requested | number: '1.2-2' }}</td>
                <td class="text-right font-semibold">
                  {{ row.paid | number: '1.2-2' }}
                  <!-- #4346–#4350 — an advance drawn down settles the invoice without a payment, so a PO can
                       show Paid 0 and still be part-settled. Called out here rather than folded into Paid,
                       which would misreport what Treasury actually disbursed against the invoices. -->
                  @if (row.advanceApplied > 0) {
                    <div class="po-settlement__subtle">+{{ row.advanceApplied | number: '1.2-2' }} {{ 'PoSettlement_AdvanceShort' | translateWF: 'advance' }}</div>
                  }
                </td>
                <td class="text-right">{{ row.remaining | number: '1.2-2' }}</td>
                <td>
                  <div class="po-settlement__progress">
                    <p-progressBar [value]="row.settlementPercent" [showValue]="false" styleClass="po-settlement__bar" />
                    <span class="po-settlement__percent">{{ row.settlementPercent | number: '1.0-0' }}%</span>
                  </div>
                </td>
                <td>
                  <p-tag [severity]="displayStatusSeverity(row.displayStatus)" [value]="displayStatusLabel(row.displayStatus)" />
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="9">{{ 'PoSettlement_NotFound' | translateWF: 'No purchase orders found' }}</td>
              </tr>
            </ng-template>
          </p-table>

          <p-paginator
            [rows]="pageSize()"
            [totalRecords]="totalRecords()"
            [first]="(currentPage() - 1) * pageSize()"
            (onPageChange)="onPageChange($event)"
            [rowsPerPageOptions]="[25, 50, 100]"
          />
        </p-card>
      </p-panel>
    </div>

    @if (summary(); as s) {
      @if (s.byProvider.length) {
        <h2 class="po-settlement__section-title">{{ 'PoSettlement_ByProvider' | translateWF: 'By provider' }}</h2>
        <div class="grid-12 gap-[1.5rem]">
          @for (provider of s.byProvider; track provider.providerReference) {
            <div class="span-12-6-4">
              <div class="provider-card">
                <div class="provider-card__head">
                  <span class="provider-card__name">{{ provider.providerName }}</span>
                  <span class="provider-card__count">{{ provider.poCount }} {{ 'PoSettlement_Pos' | translateWF: 'POs' }}</span>
                </div>
                <div class="provider-card__row">
                  <span>{{ 'PoSettlement_Total' | translateWF: 'Total' }}</span>
                  <span class="tabular-nums">{{ provider.invoiced | number: '1.2-2' }}</span>
                </div>
                <div class="provider-card__row">
                  <span>{{ 'PoSettlement_Requested' | translateWF: 'Requested' }}</span>
                  <span class="tabular-nums">{{ provider.requested | number: '1.2-2' }}</span>
                </div>
                <div class="provider-card__row">
                  <span>{{ 'PoSettlement_Paid' | translateWF: 'Paid' }}</span>
                  <span class="tabular-nums font-semibold">{{ provider.paid | number: '1.2-2' }}</span>
                </div>
                <div class="provider-card__row">
                  <span>{{ 'PoSettlement_Balance' | translateWF: 'Balance' }}</span>
                  <span class="tabular-nums">{{ provider.balance | number: '1.2-2' }}</span>
                </div>
                <p-progressBar [value]="provider.percentPaid" [showValue]="false" styleClass="po-settlement__bar mt-2" />
                <div class="provider-card__foot">
                  <span>{{ provider.percentPaid | number: '1.0-0' }}% {{ 'PoSettlement_PaidLower' | translateWF: 'paid' }}</span>
                  <span>{{ provider.settledCount }} / {{ provider.poCount }} {{ 'PoSettlement_SettledLower' | translateWF: 'settled' }}</span>
                </div>
              </div>
            </div>
          }
        </div>
      }
    }
  </div>
</main>
